import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useCountUp } from '../../hooks/useCountUp'
import { useScrollReveal } from '../../hooks/useScrollReveal'

export interface StatCounterProps extends Omit<BoxProps, 'children'> {
  value: number
  suffix?: string
  decimals?: number
  label: React.ReactNode
  source?: React.ReactNode
}

const Value = styled('div')({
  fontFamily: 'var(--ds-font)',
  fontSize: 'clamp(38px, 4.6vw, 58px)',
  fontWeight: 770,
  letterSpacing: '-0.05em',
  lineHeight: 1,
  background: 'var(--ds-gradient-spectrum, var(--ds-ink))',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
})

const Label = styled(Box)({
  marginTop: 10,
  fontSize: 12.5,
  lineHeight: 1.5,
  color: 'var(--ds-ink-muted)',
  maxWidth: 200,
})

const Source = styled(Box)({
  marginTop: 12,
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8,
  letterSpacing: '0.12em',
  color: 'var(--ds-ink-subtle)',
  textTransform: 'uppercase',
})

/** Animated count-up stat cell — fires once when scrolled into view (Proof section pattern). */
export function StatCounter({
  value,
  suffix = '',
  decimals = 0,
  label,
  source,
  ...rest
}: StatCounterProps) {
  const { ref, inView } = useScrollReveal<HTMLDivElement>({ threshold: 0.4 })
  const current = useCountUp(value, { start: inView, decimals })

  return (
    <Box ref={ref} {...rest}>
      <Value>
        {current}
        {suffix}
      </Value>
      <Label>{label}</Label>
      {source && <Source>{source}</Source>}
    </Box>
  )
}
